import { useEffect, useMemo, useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Store,
  LayoutDashboard,
  Receipt,
  Building2,
  FileSpreadsheet,
  Loader2,
  AlertCircle,
  Plus,
  Save,
  Trash2,
  Search,
  X,
  Star,
  Truck,
  Settings,
  CheckCircle,
  Package,
  Copy,
  MapPin,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { apiFetch } from '../lib/api';

function deriveRegionFromDistrict(district: string): 'HK' | 'KL' | 'NT' {
  const d = district.trim();
  if (['中西區', '灣仔區', '東區', '南區'].includes(d)) return 'HK';
  if (['油尖旺區', '深水埗區', '九龍城區', '黃大仙區', '觀塘區'].includes(d)) return 'KL';
  return 'NT';
}

interface MerchantStats {
  revenueThisMonth: number;
  orderCountThisMonth: number;
  pendingCommission: number;
}

interface MerchantOrderItem {
  name: string;
  quantity: number;
  unitPrice: number;
}

interface Consolidation {
  id: number;
  consolNum: string;
  expectedDeliveryDate: string;
  status: 'pending' | 'preparing' | 'consolidated' | 'picked_up' | 'delivered' | 'cancelled';
  pickupMethod?: 'self' | 'logistics';
  pickupTime?: string;
  logisticsType?: 'sf_direct' | 'direct_to_owner' | null;
  logisticsCompany?: string;
  logisticsContact?: string;
  deliveryAddress?: string;
  adminRemarks?: string;
  orderCount?: number;
  totalQty?: number;
  createdAt: number;
  updatedAt: number;
}

interface MerchantOrder {
  id: number;
  orderNum: string;
  totalPrice: number;
  payable?: number;
  customerName?: string;
  phone: string;
  phoneMasked?: string;
  address: string;
  estate: string;
  fulfillment: 'delivery' | 'pickup';
  createdAt: number;
  deliveryDate?: string;
  status?: string;
  paymentConfirmed?: boolean;
  orderCompleted?: boolean;
  items?: MerchantOrderItem[];
  consolidation?: Consolidation | null;
}

interface MerchantEstateSettingRow {
  id: number;
  name: string;
  region: string;
  district?: string;
  area?: string;
  estateType?: string;
  settingId?: number;
  minOrderAmount: number;
  isActive: boolean;
}

interface ReportSummary {
  revenue?: number;
  commission?: number;
  orderCount?: number;
  commissionRate?: number;
}

interface ReportData {
  summary?: ReportSummary;
  orders?: MerchantOrder[];
  commissions?: Array<{
    id?: number;
    orderId?: number;
    orderNum?: string;
    orderTotal?: number;
    commissionRate?: number;
    amount?: number;
    status?: string;
    month?: string;
  }>;
}

interface Review {
  rating: number;
  review: string;
  createdAt?: number;
}

interface RatingData {
  averageRating?: number;
  reviewCount?: number;
  latestReviews?: Review[];
}

interface MerchantSettings {
  deliveryDays: number[];
  deliveryTime: string;
  deliveryCutoffTime: string;
  deliveryCutoffDaysBefore: number;
  isClosed: boolean;
  closedUntil: string;
  closedMessage: string;
  minOrderAmount: number;
}

export interface MerchantDashboardProps {
  slug: string;
  merchantCode?: string | null;
  merchantName?: string | null;
  token?: string;
}

type TabKey = 'dashboard' | 'orders' | 'estates' | 'reviews' | 'report' | 'settings';

const TABS: { key: TabKey; labelKey: string; shortLabelKey: string; icon: React.ElementType }[] = [
  { key: 'orders', labelKey: 'merchant:tabs.orders', shortLabelKey: 'merchant:tabs.ordersShort', icon: Receipt },
  { key: 'dashboard', labelKey: 'merchant:tabs.dashboard', shortLabelKey: 'merchant:tabs.dashboardShort', icon: LayoutDashboard },
  { key: 'report', labelKey: 'merchant:tabs.report', shortLabelKey: 'merchant:tabs.reportShort', icon: FileSpreadsheet },
  { key: 'estates', labelKey: 'merchant:tabs.estates', shortLabelKey: 'merchant:tabs.estatesShort', icon: Building2 },
  { key: 'reviews', labelKey: 'merchant:tabs.reviews', shortLabelKey: 'merchant:tabs.reviewsShort', icon: Star },
  { key: 'settings', labelKey: 'merchant:tabs.settings', shortLabelKey: 'merchant:tabs.settingsShort', icon: Settings },
];

// 地區 → i18n key（label 為顯示用，value 為資料值）
const REGIONS = [
  { value: 'NT', labelKey: 'merchant:estates.regionNT' },
  { value: 'KL', labelKey: 'merchant:estates.regionKL' },
  { value: 'HK', labelKey: 'merchant:estates.regionHK' },
];

function formatDate(ts: number, locale = 'zh-HK'): string {
  return new Date(ts * 1000).toLocaleDateString(locale);
}

function formatCurrency(n: number): string {
  return `HK$${Number(n || 0).toLocaleString('zh-HK')}`;
}

function validateTime(value: string): string | null {
  if (!value) return null;
  if (!/^([0-1]?\d|2[0-3]):([0-5]\d)$/.test(value)) {
    return 'merchant:settings.cutoffFormat';
  }
  return null;
}

function maskPhone(phone: string): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  // 香港號碼：852XXXXXXXX，顯示前 4 位 + ****
  const body = digits.startsWith('852') ? digits.slice(3) : digits;
  if (body.length >= 4) {
    return `${body.slice(0, 4)} ****`;
  }
  return body;
}

function orderStatusBadgeClass(status?: string): string {
  switch (status) {
    case 'paid':
      return 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300';
    case 'completed':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300';
    case 'pending_payment':
    default:
      return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300';
  }
}

// 回傳 i18n key；未知狀態原樣顯示（資料值）
function orderStatusLabel(status: string | undefined, t: (key: string) => string): string {
  switch (status) {
    case 'paid': return t('merchant:status.paid');
    case 'completed': return t('merchant:status.completed');
    case 'pending_payment': return t('merchant:status.pendingPayment');
    default: return status || '—';
  }
}

function PhoneCell({ phone, phoneMasked }: { phone: string; phoneMasked?: string }) {
  const masked = phoneMasked || maskPhone(phone);
  return (
    <span className="group cursor-help" title={phone}>
      <span className="inline group-hover:hidden">{masked}</span>
      <span className="hidden group-hover:inline">{phone}</span>
    </span>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
      {message}
    </div>
  );
}

function Loading() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-gray-500 dark:text-gray-400">
      <Loader2 size={20} className="animate-spin" />
      <span>{t('merchant:common.loading')}</span>
    </div>
  );
}

function ErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-300 text-sm flex items-start gap-2">
      <AlertCircle size={18} className="shrink-0 mt-0.5" />
      <div className="flex-1">
        <p>{message}</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 text-sm font-bold underline hover:text-red-800 dark:hover:text-red-200"
          >
            {t('merchant:common.retry')}
          </button>
        )}
      </div>
    </div>
  );
}

export default function MerchantDashboard({ slug, merchantCode, merchantName, token }: MerchantDashboardProps) {
  const { t, i18n } = useTranslation();
  // 日期格式跟隨介面語言（zh-HK / en-GB）
  const dateLocale = i18n.language?.toLowerCase().startsWith('en') ? 'en-GB' : 'zh-HK';
  // Helper that appends merchant token to API calls for slug-based auth.
  // Merchant APIs must NOT use the admin Bearer token from localStorage;
  // they authenticate via the slug + ?token= query param.
  const merchantFetch = (path: string, options?: RequestInit) => {
    const sep = path.includes('?') ? '&' : '?';
    const url = token ? `${path}${sep}token=${encodeURIComponent(token)}` : path;
    return apiFetch(url, { ...options, skipAdminAuth: true });
  };

  const [activeTab, setActiveTab] = useState<TabKey>('orders');

  const [stats, setStats] = useState<MerchantStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState('');

  const [orders, setOrders] = useState<MerchantOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState('');

  const [ratingData, setRatingData] = useState<RatingData | null>(null);
  const [ratingLoading, setRatingLoading] = useState(false);
  const [ratingError, setRatingError] = useState('');

  const [estateSettings, setEstateSettings] = useState<MerchantEstateSettingRow[]>([]);
  const [estatesLoading, setEstatesLoading] = useState(false);
  const [estatesError, setEstatesError] = useState('');
  const [estateDrafts, setEstateDrafts] = useState<Record<number, { minOrderAmount: number; isActive: boolean }>>({});
  const [estateActionLoading, setEstateActionLoading] = useState(false);

  const [estateRegionFilter, setEstateRegionFilter] = useState<'all' | 'HK' | 'KL' | 'NT'>('all');
  const [estateDistrictFilter, setEstateDistrictFilter] = useState<string>('all');
  const [estateAreaFilter, setEstateAreaFilter] = useState<string>('all');
  const [estateTypeFilter, setEstateTypeFilter] = useState<string>('all');
  const [estateSearch, setEstateSearch] = useState('');
  const [estatePage, setEstatePage] = useState(1);
  const ESTATES_PER_PAGE = 10;

  // Reset estate pagination when filters change
  useEffect(() => {
    setEstatePage(1);
  }, [estateRegionFilter, estateDistrictFilter, estateAreaFilter, estateTypeFilter, estateSearch]);

  const [showCreateEstateModal, setShowCreateEstateModal] = useState(false);
  const [createEstateForm, setCreateEstateForm] = useState({ name: '', region: 'HK' as 'HK' | 'KL' | 'NT', district: '', area: '', estateType: '' });
  const [createEstateLoading, setCreateEstateLoading] = useState(false);

  const [reportMonth, setReportMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');

  const [expandedOrderNum, setExpandedOrderNum] = useState<string | null>(null);

  const [settings, setSettings] = useState<MerchantSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [settingsSuccess, setSettingsSuccess] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);

  const [consolidations, setConsolidations] = useState<Consolidation[]>([]);
  const [pickupAddress, setPickupAddress] = useState('');
  const [consolidationsLoading, setConsolidationsLoading] = useState(false);
  const [consolidationsError, setConsolidationsError] = useState('');
  const [selectedOrderNums, setSelectedOrderNums] = useState<Set<string>>(new Set());
  const [showConsolidationModal, setShowConsolidationModal] = useState(false);
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [pickupMethod, setPickupMethod] = useState<'self' | 'logistics'>('self');
  const [pickupTime, setPickupTime] = useState('');
  const [logisticsType, setLogisticsType] = useState<'sf_direct' | 'direct_to_owner' | ''>('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [savedAddresses, setSavedAddresses] = useState<string[]>([]);
  const [logisticsCompany, setLogisticsCompany] = useState('');
  const [logisticsContact, setLogisticsContact] = useState('');
  const [nextDeliveryDate, setNextDeliveryDate] = useState<string | null>(null);
  const [createConsolidationLoading, setCreateConsolidationLoading] = useState(false);

  const fetchStats = async () => {
    setStatsLoading(true);
    setStatsError('');
    try {
      const res = await merchantFetch(`/api/public/merchant/${encodeURIComponent(slug)}/stats`);
      if (!res.ok) throw new Error(t('merchant:common.loadFailedStatus', { status: res.status }));
      const data = await res.json();
      setStats({
        revenueThisMonth: Number(data.revenueThisMonth || data.revenue_this_month || 0),
        orderCountThisMonth: Number(data.orderCountThisMonth || data.order_count_this_month || 0),
        pendingCommission: Number(data.pendingCommission || data.pending_commission || 0),
      });
    } catch (e) {
      setStatsError(e instanceof Error ? e.message : t('merchant:common.loadFailed'));
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchOrders = async () => {
    setOrdersLoading(true);
    setOrdersError('');
    try {
      const res = await merchantFetch(`/api/public/merchant/${encodeURIComponent(slug)}/orders`);
      if (!res.ok) throw new Error(t('merchant:common.loadFailedStatus', { status: res.status }));
      const data = await res.json();
      setOrders(Array.isArray(data) ? data : data.orders || []);
    } catch (e) {
      setOrdersError(e instanceof Error ? e.message : t('merchant:common.loadFailed'));
    } finally {
      setOrdersLoading(false);
    }
  };

  const fetchConsolidatableOrders = async () => {
    try {
      const res = await merchantFetch(`/api/public/merchant/${encodeURIComponent(slug)}/orders?consolidatable=true`);
      if (!res.ok) throw new Error(t('merchant:common.loadFailedStatus', { status: res.status }));
      const data = await res.json();
      setNextDeliveryDate(data.nextDeliveryDate || null);
      return data.orders || [];
    } catch (e) {
      setOrdersError(e instanceof Error ? e.message : t('merchant:common.loadFailed'));
      return [];
    }
  };

  const fetchConsolidations = async () => {
    setConsolidationsLoading(true);
    setConsolidationsError('');
    try {
      const res = await merchantFetch(`/api/public/merchant/${encodeURIComponent(slug)}/consolidations`);
      if (!res.ok) throw new Error(t('merchant:common.loadFailedStatus', { status: res.status }));
      const data = await res.json();
      const list: Consolidation[] = data.consolidations || [];
      setConsolidations(list);
      setPickupAddress(data.pickupAddress || '');
      setSavedAddresses(Array.isArray(data.savedAddresses) ? data.savedAddresses : []);
    } catch (e) {
      setConsolidationsError(e instanceof Error ? e.message : t('merchant:common.loadFailed'));
    } finally {
      setConsolidationsLoading(false);
    }
  };

  const handleCreateConsolidation = async () => {
    if (selectedOrderNums.size === 0) {
      alert(t('merchant:consolidation.needOrder'));
      return;
    }
    if (!expectedDeliveryDate) {
      alert(t('merchant:consolidation.needDate'));
      return;
    }
    if (pickupMethod === 'self' && !pickupTime) {
      alert(t('merchant:consolidation.needPickupTime'));
      return;
    }
    if (pickupMethod === 'logistics' && !logisticsType) {
      alert(t('merchant:consolidation.needLogisticsType'));
      return;
    }
    if (pickupMethod === 'logistics' && logisticsType === 'direct_to_owner' && !deliveryAddress.trim()) {
      alert(t('merchant:consolidation.needDeliveryAddress'));
      return;
    }
    // 直送店主：順豐由發貨方安排，無需填寫物流公司名稱及聯絡
    if (pickupMethod === 'logistics' && logisticsType === 'direct_to_owner' && !deliveryAddress.trim()) {
      alert(t('merchant:consolidation.needDeliveryAddress'));
      return;
    }
    setCreateConsolidationLoading(true);
    try {
      const res = await merchantFetch(`/api/public/merchant/${encodeURIComponent(slug)}/consolidations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderIds: Array.from(selectedOrderNums),
          expectedDeliveryDate,
          pickupMethod,
          pickupTime,
          logisticsType,
          deliveryAddress,
          logisticsCompany,
          logisticsContact,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || t('merchant:common.createFailed'));
      setShowConsolidationModal(false);
      setSelectedOrderNums(new Set());
      setPickupMethod('self');
      setPickupTime('');
      setLogisticsType('');
      setDeliveryAddress('');
      setLogisticsCompany('');
      setLogisticsContact('');
      fetchOrders();
      fetchConsolidations();
    } catch (e) {
      alert(e instanceof Error ? e.message : t('merchant:common.createFailed'));
    } finally {
      setCreateConsolidationLoading(false);
    }
  };

  const fetchRating = async () => {
    setRatingLoading(true);
    setRatingError('');
    try {
      const res = await merchantFetch(`/api/public/merchant/${encodeURIComponent(slug)}/rating`);
      if (!res.ok) throw new Error(t('merchant:common.loadFailedStatus', { status: res.status }));
      const data: RatingData = await res.json();
      setRatingData(data);
    } catch (e) {
      setRatingError(e instanceof Error ? e.message : t('merchant:common.loadFailed'));
    } finally {
      setRatingLoading(false);
    }
  };

  const fetchSettings = async () => {
    setSettingsLoading(true);
    setSettingsError('');
    try {
      const res = await merchantFetch(`/api/public/merchant/${encodeURIComponent(slug)}/stats`);
      if (!res.ok) throw new Error(t('merchant:common.loadFailedStatus', { status: res.status }));
      const data = await res.json();
      const m = data.merchant || {};
      setSettings({
        deliveryDays: Array.isArray(m.deliveryDays) ? m.deliveryDays : (m.delivery_days || []),
        deliveryTime: m.deliveryTime || m.delivery_time || '16:00-18:00',
        deliveryCutoffTime: m.deliveryCutoffTime || m.delivery_cutoff_time || '20:00',
        deliveryCutoffDaysBefore: Number(m.deliveryCutoffDaysBefore || m.delivery_cutoff_days_before || 1),
        isClosed: !!(m.isClosed || m.is_closed),
        closedUntil: m.closedUntil || m.closed_until || '',
        closedMessage: m.closedMessage || m.closed_message || '',
        minOrderAmount: Number(m.minOrderAmount ?? m.min_order_amount ?? 0),
      });
    } catch (e) {
      setSettingsError(e instanceof Error ? e.message : t('merchant:common.loadFailed'));
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!settings) return;
    const timeError = validateTime(settings.deliveryCutoffTime);
    if (timeError) {
      setSettingsError(t(timeError));
      return;
    }
    setSettingsSaving(true);
    setSettingsError('');
    setSettingsSuccess('');
    try {
      const res = await merchantFetch(`/api/public/merchant/${encodeURIComponent(slug)}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          deliveryDays: settings.deliveryDays,
          deliveryTime: settings.deliveryTime.trim() || null,
          deliveryCutoffTime: settings.deliveryCutoffTime.trim() || null,
          deliveryCutoffDaysBefore: Number(settings.deliveryCutoffDaysBefore) || 1,
          isClosed: settings.isClosed,
          closedUntil: settings.closedUntil.trim() || null,
          closedMessage: settings.closedMessage.trim() || null,
          minOrderAmount: Number(settings.minOrderAmount) || 0,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || t('merchant:common.saveFailed'));
      setSettingsSuccess(t('merchant:settings.savedNotice'));
      setTimeout(() => setSettingsSuccess(''), 3000);
    } catch (e) {
      setSettingsError(e instanceof Error ? e.message : t('merchant:common.saveFailed'));
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleMarkDelivered = async (orderId: number, orderNum: string) => {
    if (!window.confirm(t('merchant:orders.confirmDelivered', { orderNum }))) return;
    try {
      // 後端 :id 參數係 numeric order id，唔係 orderNum
      const res = await merchantFetch(`/api/public/merchant/${encodeURIComponent(slug)}/orders/${orderId}/delivered`, {
        method: 'POST',
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || t('merchant:orders.markFailed'));
      fetchOrders();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('merchant:orders.markFailed'));
    }
  };

  const fetchEstates = async () => {
    setEstatesLoading(true);
    setEstatesError('');
    try {
      const res = await merchantFetch(`/api/public/merchant/${encodeURIComponent(slug)}/estates`);
      if (!res.ok) throw new Error(t('merchant:common.loadFailedStatus', { status: res.status }));
      const data = await res.json();
      const rows = Array.isArray(data) ? data : data.estates || [];
      setEstateSettings(rows);
      const drafts: Record<number, { minOrderAmount: number; isActive: boolean }> = {};
      rows.forEach((row: MerchantEstateSettingRow) => {
        drafts[row.id] = { minOrderAmount: row.minOrderAmount || 0, isActive: row.isActive };
      });
      setEstateDrafts(drafts);
    } catch (e) {
      setEstatesError(e instanceof Error ? e.message : t('merchant:common.loadFailed'));
    } finally {
      setEstatesLoading(false);
    }
  };

  const filteredEstateSettings = useMemo(() => {
    const term = estateSearch.trim().toLowerCase();
    return estateSettings
      .filter((row) => {
        const matchesRegion = estateRegionFilter === 'all' || row.region === estateRegionFilter;
        const matchesDistrict = estateDistrictFilter === 'all' || row.district === estateDistrictFilter;
        const matchesArea = estateAreaFilter === 'all' || row.area === estateAreaFilter;
        const matchesType = estateTypeFilter === 'all' || row.estateType === estateTypeFilter;
        const matchesSearch = !term ||
          row.name.toLowerCase().includes(term) ||
          (row.district && row.district.toLowerCase().includes(term)) ||
          (row.area && row.area.toLowerCase().includes(term)) ||
          (row.estateType && row.estateType.toLowerCase().includes(term));
        return matchesRegion && matchesDistrict && matchesArea && matchesType && matchesSearch;
      })
      .sort((a, b) => {
        // 已啟用的屋苑排在最前
        if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
        return a.name.localeCompare(b.name, 'zh-HK');
      });
  }, [estateSettings, estateRegionFilter, estateDistrictFilter, estateAreaFilter, estateTypeFilter, estateSearch]);

  const estateTotalPages = Math.max(1, Math.ceil(filteredEstateSettings.length / ESTATES_PER_PAGE));
  const paginatedEstateSettings = useMemo(() => {
    const start = (estatePage - 1) * ESTATES_PER_PAGE;
    return filteredEstateSettings.slice(start, start + ESTATES_PER_PAGE);
  }, [filteredEstateSettings, estatePage]);

  const districtOptions = useMemo(() => {
    const source = estateRegionFilter === 'all' ? estateSettings : estateSettings.filter((r) => r.region === estateRegionFilter);
    const districts = new Set<string>();
    source.forEach((row) => {
      if (row.district) districts.add(row.district);
    });
    return ['全部', ...Array.from(districts).sort()];
  }, [estateSettings, estateRegionFilter]);

  const areaOptions = useMemo(() => {
    let source = estateSettings;
    if (estateRegionFilter !== 'all') source = source.filter((r) => r.region === estateRegionFilter);
    if (estateDistrictFilter !== 'all') source = source.filter((r) => r.district === estateDistrictFilter);
    const areas = new Set<string>();
    source.forEach((row) => {
      if (row.area) areas.add(row.area);
    });
    return ['全部', ...Array.from(areas).sort()];
  }, [estateSettings, estateRegionFilter, estateDistrictFilter]);

  const estateTypeOptions = useMemo(() => {
    const types = new Set<string>();
    estateSettings.forEach((row) => {
      if (row.estateType) types.add(row.estateType);
    });
    return ['全部', ...Array.from(types).sort()];
  }, [estateSettings]);

  useEffect(() => {
    fetchStats();
    fetchRating();
  }, [slug]);

  useEffect(() => {
    if (activeTab === 'orders') {
      fetchOrders();
      fetchConsolidations();
      fetchConsolidatableOrders().then(list => {
        if (list.length > 0 && !expectedDeliveryDate) {
          setExpectedDeliveryDate(list[0].deliveryDate || nextDeliveryDate || '');
        }
      });
    }
    if (activeTab === 'estates') {
      fetchEstates();
      fetchSettings();
    }
    if (activeTab === 'reviews') fetchRating();
    if (activeTab === 'settings') fetchSettings();
  }, [activeTab, slug]);

  const updateEstateDraft = (id: number, patch: Partial<{ minOrderAmount: number; isActive: boolean }>) => {
    setEstateDrafts(prev => ({
      ...prev,
      [id]: { minOrderAmount: prev[id]?.minOrderAmount ?? 0, isActive: prev[id]?.isActive ?? false, ...patch },
    }));
  };

  const handleJoinEstate = async (row: MerchantEstateSettingRow) => {
    const draft = estateDrafts[row.id] || { minOrderAmount: 0, isActive: false };
    setEstateActionLoading(true);
    try {
      const res = await merchantFetch(`/api/public/merchant/${encodeURIComponent(slug)}/estates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ estateId: row.id, minOrderAmount: settings?.minOrderAmount ?? draft.minOrderAmount, isActive: draft.isActive }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || t('merchant:estates.joinFailed'));
      fetchEstates();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('merchant:estates.joinFailed'));
    } finally {
      setEstateActionLoading(false);
    }
  };

  const handleSaveEstateSetting = async (row: MerchantEstateSettingRow) => {
    if (!row.settingId) return;
    const draft = estateDrafts[row.id] || { minOrderAmount: row.minOrderAmount || 0, isActive: row.isActive };
    setEstateActionLoading(true);
    try {
      const res = await merchantFetch(`/api/public/merchant/${encodeURIComponent(slug)}/estates/${row.settingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ minOrderAmount: settings?.minOrderAmount ?? draft.minOrderAmount, isActive: draft.isActive }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || t('merchant:common.saveFailed'));
      fetchEstates();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('merchant:common.saveFailed'));
    } finally {
      setEstateActionLoading(false);
    }
  };

  const handleRemoveEstateSetting = async (row: MerchantEstateSettingRow) => {
    if (!row.settingId) return;
    if (!window.confirm(t('merchant:estates.confirmRemove', { name: row.name }))) return;
    setEstateActionLoading(true);
    try {
      const res = await merchantFetch(`/api/public/merchant/${encodeURIComponent(slug)}/estates/${row.settingId}`, {
        method: 'DELETE',
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || t('merchant:estates.removeFailed'));
      fetchEstates();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('merchant:estates.removeFailed'));
    } finally {
      setEstateActionLoading(false);
    }
  };

  const handleCreateEstate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createEstateForm.name.trim() || !createEstateForm.region) return;
    setCreateEstateLoading(true);
    try {
      const res = await merchantFetch(`/api/public/merchant/${encodeURIComponent(slug)}/custom-estates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: createEstateForm.name.trim(),
          district: createEstateForm.district.trim() || undefined,
          area: createEstateForm.area.trim() || undefined,
          estate_type: createEstateForm.estateType.trim() || undefined,
          minOrderAmount: 0,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || t('merchant:estates.createFailed'));
      setCreateEstateForm({ name: '', region: 'HK', district: '', area: '', estateType: '' });
      setShowCreateEstateModal(false);
      fetchEstates();
    } catch (err) {
      alert(err instanceof Error ? err.message : t('merchant:estates.createFailed'));
    } finally {
      setCreateEstateLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!reportMonth) return;
    setReportLoading(true);
    setReportError('');
    try {
      const res = await merchantFetch(
        `/api/public/merchant/${encodeURIComponent(slug)}/report?month=${encodeURIComponent(reportMonth)}`
      );
      if (!res.ok) throw new Error(t('merchant:common.loadFailedStatus', { status: res.status }));
      const data: ReportData = await res.json();

      const wb = XLSX.utils.book_new();
      // 表頭走 i18n（EN 模式匯出 EN 報告）
      const L = (key: string) => t(`merchant:commission.xls.${key}`);

      // 摘要 sheet
      const summaryRows = [
        { [L('item')]: L('month'), [L('value')]: reportMonth },
        { [L('item')]: L('merchantCode'), [L('value')]: merchantCode || '' },
        { [L('item')]: L('merchantName'), [L('value')]: merchantName || '' },
        { [L('item')]: L('revenue'), [L('value')]: data.summary?.revenue ?? 0 },
        { [L('item')]: L('orderCount'), [L('value')]: data.summary?.orderCount ?? 0 },
        { [L('item')]: L('commissionRate'), [L('value')]: data.summary?.commissionRate ?? 15 },
        { [L('item')]: L('commission'), [L('value')]: data.summary?.commission ?? 0 },
      ];
      const summaryWs = XLSX.utils.json_to_sheet(summaryRows);
      XLSX.utils.book_append_sheet(wb, summaryWs, L('summarySheet'));

      // 訂單明細 sheet
      const orderRows = (data.orders || []).map(o => ({
        [L('orderNum')]: o.orderNum,
        [L('date')]: o.createdAt ? formatDate(o.createdAt, dateLocale) : '',
        [L('customerName')]: o.customerName || '',
        [L('phone')]: o.phone || '',
        [L('estate')]: o.estate,
        [L('fulfillment')]: o.fulfillment === 'pickup' ? t('merchant:orders.fulfillmentPickup') : t('merchant:orders.fulfillmentDelivery'),
        [L('address')]: o.address,
        [L('totalPrice')]: o.totalPrice,
        [L('payable')]: o.payable ?? o.totalPrice,
      }));
      if (orderRows.length > 0) {
        const ordersWs = XLSX.utils.json_to_sheet(orderRows);
        XLSX.utils.book_append_sheet(wb, ordersWs, L('ordersSheet'));
      }

      // 佣金明細 sheet（如後端有提供）
      const commissionRows = (data.commissions || []).map(c => ({
        [L('commissionId')]: c.id,
        [L('orderId')]: c.orderId,
        [L('orderNum')]: c.orderNum,
        [L('totalPrice')]: c.orderTotal,
        [L('commissionRateCol')]: c.commissionRate,
        [L('commissionAmount')]: c.amount,
        [L('status')]: c.status,
        [L('month')]: c.month,
      }));
      if (commissionRows.length > 0) {
        const commissionsWs = XLSX.utils.json_to_sheet(commissionRows);
        XLSX.utils.book_append_sheet(wb, commissionsWs, L('commissionsSheet'));
      }

      const filename = `merchant-report-${merchantCode || slug}-${reportMonth}.xlsx`;
      XLSX.writeFile(wb, filename);
    } catch (e) {
      setReportError(e instanceof Error ? e.message : t('merchant:common.downloadFailed'));
    } finally {
      setReportLoading(false);
    }
  };

  const renderDashboard = () => {
    if (statsLoading) return <Loading />;
    if (statsError) return <ErrorBox message={statsError} onRetry={fetchStats} />;
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('merchant:dashboard.revenue')}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(stats?.revenueThisMonth || 0)}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('merchant:dashboard.orderCount')}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats?.orderCountThisMonth || 0}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('merchant:dashboard.pendingCommission')}</p>
            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{formatCurrency(stats?.pendingCommission || 0)}</p>
          </div>
        </div>
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800 rounded-2xl p-5">
          <p className="text-sm text-orange-800 dark:text-orange-200 font-bold mb-1">{t('merchant:dashboard.noteTitle')}</p>
          <p className="text-sm text-orange-700 dark:text-orange-300">
            {t('merchant:dashboard.noteBody')}
          </p>
        </div>
      </div>
    );
  };

  const renderOrders = () => {
    if (ordersLoading) return <Loading />;
    if (ordersError) return <ErrorBox message={ordersError} onRetry={fetchOrders} />;

    const consolidatableOrders = orders.filter(o =>
      o.paymentConfirmed && !o.orderCompleted && !o.consolidation
    );
    const currentCycleOrders = consolidatableOrders.filter(o =>
      o.deliveryDate === nextDeliveryDate
    );
    const allSelected = currentCycleOrders.length > 0 && currentCycleOrders.every(o => selectedOrderNums.has(o.orderNum));

    return (
      <div className="space-y-6">
        {/* Action bar */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('merchant:consolidation.nextDelivery')}
                <span className="ml-2 font-bold text-gray-900 dark:text-white">
                  {nextDeliveryDate || '—'}
                </span>
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                {t('merchant:consolidation.selectedCount', { count: selectedOrderNums.size })}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  if (allSelected) {
                    const next = new Set(selectedOrderNums);
                    currentCycleOrders.forEach(o => next.delete(o.orderNum));
                    setSelectedOrderNums(next);
                  } else {
                    const next = new Set(selectedOrderNums);
                    currentCycleOrders.forEach(o => next.add(o.orderNum));
                    setSelectedOrderNums(next);
                  }
                }}
                disabled={currentCycleOrders.length === 0}
                className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {allSelected ? t('merchant:consolidation.deselectAll') : t('merchant:consolidation.selectAll')}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (selectedOrderNums.size === 0) {
                    alert(t('merchant:consolidation.selectFirst'));
                    return;
                  }
                  setExpectedDeliveryDate(nextDeliveryDate || '');
                  setPickupMethod('self');
                  setShowConsolidationModal(true);
                }}
                disabled={selectedOrderNums.size === 0}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold rounded-lg bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                <Package size={16} /> {t('merchant:consolidation.createBtn')}
              </button>
            </div>
          </div>
        </div>

        {orders.length === 0 ? (
          <EmptyState message={t('merchant:orders.empty')} />
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-3 py-3 text-left font-semibold text-gray-700 dark:text-gray-200 w-10"></th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">{t('merchant:orders.colOrder')}</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">{t('merchant:orders.colStatus')}</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">{t('merchant:orders.colDeliveryDate')}</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">{t('merchant:orders.colCustomer')}</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">{t('merchant:orders.colEstateMethod')}</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">{t('merchant:orders.colConsol')}</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">{t('merchant:orders.colAddress')}</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-200">{t('merchant:orders.colAmount')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {orders.map(o => {
                    const expanded = expandedOrderNum === o.orderNum;
                    const isConsolidatable = o.paymentConfirmed && !o.orderCompleted && !o.consolidation;
                    const canMarkDelivered = o.paymentConfirmed && !o.orderCompleted && o.consolidation?.status === 'picked_up';
                    return (
                      <Fragment key={o.orderNum}>
                        <tr
                          className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                          onClick={() => setExpandedOrderNum(expanded ? null : o.orderNum)}
                        >
                          <td className="px-3 py-3 align-top" onClick={(e) => e.stopPropagation()}>
                            {isConsolidatable && (
                              <input
                                type="checkbox"
                                checked={selectedOrderNums.has(o.orderNum)}
                                onChange={(e) => {
                                  const next = new Set(selectedOrderNums);
                                  if (e.target.checked) next.add(o.orderNum);
                                  else next.delete(o.orderNum);
                                  setSelectedOrderNums(next);
                                }}
                                className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                              />
                            )}
                          </td>
                          <td className="px-4 py-3 align-top">
                            <div className="font-bold text-gray-900 dark:text-white">#{o.orderNum}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{formatDate(o.createdAt, dateLocale)}</div>
                          </td>
                          <td className="px-4 py-3 align-top">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${orderStatusBadgeClass(o.status)}`}>
                              {orderStatusLabel(o.status, t)}
                            </span>
                          </td>
                          <td className="px-4 py-3 align-top text-gray-700 dark:text-gray-300">
                            {o.deliveryDate || '—'}
                          </td>
                          <td className="px-4 py-3 align-top">
                            <div className="text-gray-900 dark:text-white">{o.customerName || '—'}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 group">
                              <PhoneCell phone={o.phone} phoneMasked={o.phoneMasked} />
                            </div>
                          </td>
                          <td className="px-4 py-3 align-top">
                            <div className="text-gray-900 dark:text-white">{o.estate}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{o.fulfillment === 'pickup' ? t('merchant:orders.fulfillmentPickup') : t('merchant:orders.fulfillmentDelivery')}</div>
                          </td>
                          <td className="px-4 py-3 align-top">
                            {o.consolidation ? (
                              <div className="space-y-1">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
                                  o.consolidation.status === 'picked_up'
                                    ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                                    : o.consolidation.status === 'delivered'
                                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                                    : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300'
                                }`}>
                                  {o.consolidation.consolNum}
                                </span>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {t(`merchant:consolidation.status.${o.consolidation.status}`)}
                                </div>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400 dark:text-gray-500">{t('merchant:consolidation.notInConsol')}</span>
                            )}
                          </td>
                          <td className="px-4 py-3 align-top text-gray-700 dark:text-gray-300 whitespace-pre-line">{o.address}</td>
                          <td className="px-4 py-3 align-top text-right">
                            <div className="font-bold text-gray-900 dark:text-white">{formatCurrency(o.payable ?? o.totalPrice)}</div>
                            {typeof o.payable === 'number' && o.payable !== o.totalPrice && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 line-through">{formatCurrency(o.totalPrice)}</div>
                            )}
                          </td>
                        </tr>
                        {expanded && (
                          <tr>
                            <td className="px-3 py-3 bg-gray-50 dark:bg-gray-700/40"></td>
                            <td colSpan={8} className="px-4 py-3 bg-gray-50 dark:bg-gray-700/40">
                              <div className="ml-2">
                                <p className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-2">{t('merchant:orders.itemsTitle')}</p>
                                {(o.items || []).length === 0 ? (
                                  <p className="text-xs text-gray-400 dark:text-gray-500">{t('merchant:orders.noItems')}</p>
                                ) : (
                                  <ul className="space-y-1">
                                    {(o.items || []).map((it, idx) => (
                                      <li key={idx} className="flex justify-between text-sm text-gray-700 dark:text-gray-200">
                                        <span>{it.name} × {it.quantity}</span>
                                        <span>{formatCurrency(it.unitPrice * it.quantity)}</span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                                {canMarkDelivered && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMarkDelivered(o.id, o.orderNum);
                                    }}
                                    className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-bold rounded-lg transition"
                                  >
                                    <Truck size={12} /> {t('merchant:orders.markDelivered')}
                                  </button>
                                )}
                                {o.paymentConfirmed && !o.orderCompleted && !o.consolidation && (
                                  <p className="mt-3 text-xs text-orange-600 dark:text-orange-400">
                                    {t('merchant:orders.notInConsolHint')}
                                  </p>
                                )}
                                {o.consolidation && o.consolidation.status !== 'picked_up' && o.consolidation.status !== 'delivered' && (
                                  <p className="mt-3 text-xs text-yellow-600 dark:text-yellow-400">
                                    {t('merchant:consolidation.waitPickup', { status: t(`merchant:consolidation.status.${o.consolidation.status}`) })}
                                  </p>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-700">
              {orders.map(o => {
                const expanded = expandedOrderNum === o.orderNum;
                const isConsolidatable = o.paymentConfirmed && !o.orderCompleted && !o.consolidation;
                const canMarkDelivered = o.paymentConfirmed && !o.orderCompleted && o.consolidation?.status === 'picked_up';
                return (
                  <div
                    key={o.orderNum}
                    className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 active:bg-gray-100 dark:active:bg-gray-700 transition"
                    onClick={() => setExpandedOrderNum(expanded ? null : o.orderNum)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="pt-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                        {isConsolidatable && (
                          <input
                            type="checkbox"
                            checked={selectedOrderNums.has(o.orderNum)}
                            onChange={(e) => {
                              const next = new Set(selectedOrderNums);
                              if (e.target.checked) next.add(o.orderNum);
                              else next.delete(o.orderNum);
                              setSelectedOrderNums(next);
                            }}
                            className="w-5 h-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-bold text-gray-900 dark:text-white">#{o.orderNum}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{formatDate(o.createdAt, dateLocale)}</div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-bold text-gray-900 dark:text-white">{formatCurrency(o.payable ?? o.totalPrice)}</div>
                            {typeof o.payable === 'number' && o.payable !== o.totalPrice && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 line-through">{formatCurrency(o.totalPrice)}</div>
                            )}
                          </div>
                        </div>

                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${orderStatusBadgeClass(o.status)}`}>
                            {orderStatusLabel(o.status, t)}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{o.deliveryDate || '—'}</span>
                        </div>

                        <div className="mt-2">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{o.customerName || '—'}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            <PhoneCell phone={o.phone} phoneMasked={o.phoneMasked} />
                          </div>
                        </div>

                        <div className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                          <span className="font-medium text-gray-900 dark:text-white">{o.estate}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                            {o.fulfillment === 'pickup' ? t('merchant:orders.fulfillmentPickup') : t('merchant:orders.fulfillmentDelivery')}
                          </span>
                        </div>
                        <div className="mt-1 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">{o.address}</div>

                        {o.consolidation ? (
                          <div className="mt-2 flex items-center gap-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
                              o.consolidation.status === 'picked_up'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                                : o.consolidation.status === 'delivered'
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300'
                            }`}>
                              {o.consolidation.consolNum}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {t(`merchant:consolidation.status.${o.consolidation.status}`)}
                            </span>
                          </div>
                        ) : (
                          <div className="mt-2 text-xs text-gray-400 dark:text-gray-500">{t('merchant:consolidation.notInConsol')}</div>
                        )}

                        {expanded && (
                          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                            <p className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-2">{t('merchant:orders.itemsTitle')}</p>
                            {(o.items || []).length === 0 ? (
                              <p className="text-xs text-gray-400 dark:text-gray-500">{t('merchant:orders.noItems')}</p>
                            ) : (
                              <ul className="space-y-1">
                                {(o.items || []).map((it, idx) => (
                                  <li key={idx} className="flex justify-between text-sm text-gray-700 dark:text-gray-200">
                                    <span>{it.name} × {it.quantity}</span>
                                    <span>{formatCurrency(it.unitPrice * it.quantity)}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                            {canMarkDelivered && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkDelivered(o.id, o.orderNum);
                                }}
                                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-bold rounded-lg transition"
                              >
                                <Truck size={12} /> {t('merchant:orders.markDelivered')}
                              </button>
                            )}
                            {o.paymentConfirmed && !o.orderCompleted && !o.consolidation && (
                              <p className="mt-3 text-xs text-orange-600 dark:text-orange-400">
                                {t('merchant:orders.notInConsolHint')}
                              </p>
                            )}
                            {o.consolidation && o.consolidation.status !== 'picked_up' && o.consolidation.status !== 'delivered' && (
                              <p className="mt-3 text-xs text-yellow-600 dark:text-yellow-400">
                                {t('merchant:consolidation.waitPickup', { status: t(`merchant:consolidation.status.${o.consolidation.status}`) })}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {renderConsolidations()}

        {showConsolidationModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{t('merchant:consolidation.modalTitle')}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {t('merchant:consolidation.modalSelectedCount', { count: selectedOrderNums.size })}
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('merchant:consolidation.expectedDateLabel')}</label>
                <input
                  type="date"
                  value={expectedDeliveryDate}
                  min={nextDeliveryDate || ''}
                  onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('merchant:consolidation.pickupMethodLabel')}</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setPickupMethod('self')}
                    className={`px-4 py-3 text-sm font-bold rounded-lg border transition ${
                      pickupMethod === 'self'
                        ? 'bg-orange-600 text-white border-orange-600'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {t('merchant:consolidation.self')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setPickupMethod('logistics')}
                    className={`px-4 py-3 text-sm font-bold rounded-lg border transition ${
                      pickupMethod === 'logistics'
                        ? 'bg-orange-600 text-white border-orange-600'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {t('merchant:consolidation.logistics')}
                  </button>
                </div>
              </div>

              {pickupMethod === 'self' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('merchant:consolidation.pickupTimeLabel')}</label>
                  <select
                    value={pickupTime}
                    onChange={(e) => setPickupTime(e.target.value)}
                    className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                    <option value="">{t('merchant:consolidation.pickupTimePlaceholder')}</option>
                    {Array.from({ length: 19 }, (_, i) => {
                      const hour = Math.floor(i / 2) + 9;
                      const minute = i % 2 === 0 ? '00' : '30';
                      const value = `${String(hour).padStart(2, '0')}:${minute}`;
                      return <option key={value} value={value}>{value}</option>;
                    })}
                  </select>
                </div>
              )}

              {pickupMethod === 'logistics' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('merchant:consolidation.logisticsTypeLabel')}</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setLogisticsType('sf_direct')}
                      className={`px-4 py-3 text-sm font-bold rounded-lg border transition ${
                        logisticsType === 'sf_direct'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {t('merchant:consolidation.sfDirect')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setLogisticsType('direct_to_owner')}
                      className={`px-4 py-3 text-sm font-bold rounded-lg border transition ${
                        logisticsType === 'direct_to_owner'
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {t('merchant:consolidation.directToOwner')}
                    </button>
                  </div>
                </div>
              )}

              {pickupMethod === 'logistics' && logisticsType === 'direct_to_owner' && (
                <div className="space-y-3 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('merchant:consolidation.deliveryAddressLabel')}</label>
                    {savedAddresses.length > 0 && (
                      <select
                        value={savedAddresses.includes(deliveryAddress) ? deliveryAddress : ''}
                        onChange={(e) => {
                          if (e.target.value === '__new__') {
                            setDeliveryAddress('');
                          } else if (e.target.value) {
                            setDeliveryAddress(e.target.value);
                          }
                        }}
                        className="w-full p-2.5 mb-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      >
                        <option value="">{t('merchant:consolidation.savedAddressPlaceholder')}</option>
                        {savedAddresses.map((addr) => (
                          <option key={addr} value={addr}>{addr}</option>
                        ))}
                        <option value="__new__">{t('merchant:consolidation.newAddressOption')}</option>
                      </select>
                    )}
                    <input
                      type="text"
                      value={deliveryAddress}
                      onChange={(e) => setDeliveryAddress(e.target.value)}
                      placeholder={t('merchant:consolidation.deliveryAddressPlaceholder')}
                      className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('merchant:consolidation.directToOwnerSfHint')}
                  </p>
                </div>
              )}

              {pickupMethod === 'logistics' && logisticsType === 'sf_direct' && (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-800 dark:text-blue-300">
                  {t('merchant:consolidation.sfDirectHint')}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowConsolidationModal(false)}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  {t('merchant:consolidation.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleCreateConsolidation}
                  disabled={createConsolidationLoading}
                  className="px-4 py-2 text-sm font-bold rounded-lg bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50"
                >
                  {createConsolidationLoading ? t('merchant:consolidation.processing') : t('merchant:consolidation.confirmCreate')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const [copiedPickup, setCopiedPickup] = useState(false);

  const renderConsolidations = () => {
    if (consolidationsLoading) return <Loading />;
    if (consolidationsError) return <ErrorBox message={consolidationsError} onRetry={fetchConsolidations} />;
    return (
      <div className="space-y-4">
        {pickupAddress && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <MapPin className="shrink-0 text-blue-600 dark:text-blue-400 mt-0.5" size={20} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-blue-800 dark:text-blue-300">{t('merchant:consolidation.pickupPointTitle')}</div>
                <div className="text-sm text-blue-900 dark:text-blue-200 mt-1 break-words">{pickupAddress}</div>
              </div>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(pickupAddress);
                    setCopiedPickup(true);
                    setTimeout(() => setCopiedPickup(false), 2000);
                  } catch { /* ignore */ }
                }}
                className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
              >
                {copiedPickup ? <CheckCircle size={14} /> : <Copy size={14} />}
                {copiedPickup ? t('merchant:consolidation.copied') : t('merchant:consolidation.copyAddress')}
              </button>
            </div>
          </div>
        )}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
            <h3 className="font-bold text-gray-800 dark:text-gray-200">{t('merchant:consolidation.sectionTitle')}</h3>
          </div>
          {consolidations.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
            {t('merchant:consolidation.empty')}
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {consolidations.map(c => (
              <div key={c.consolNum} className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <div className="font-bold text-gray-900 dark:text-white">{c.consolNum}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {t('merchant:consolidation.expectedDate', { date: c.expectedDeliveryDate })}
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${
                    c.status === 'picked_up'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                      : c.status === 'delivered'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                      : c.status === 'cancelled'
                      ? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300'
                  }`}>
                    {t(`merchant:consolidation.status.${c.status}`)}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
                    c.pickupMethod === 'logistics'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                  }`}>
                    {c.pickupMethod === 'logistics' ? t('merchant:consolidation.logistics') : t('merchant:consolidation.self')}
                  </span>
                  {c.pickupMethod === 'self' && c.pickupTime && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {t('merchant:consolidation.pickupTimeShort', { time: c.pickupTime })}
                    </span>
                  )}
                  {c.pickupMethod === 'logistics' && c.logisticsType === 'sf_direct' && (
                    <span className="text-xs text-blue-600 dark:text-blue-400 font-bold">
                      {t('merchant:consolidation.sfDirect')}
                    </span>
                  )}
                  {c.pickupMethod === 'logistics' && c.logisticsType === 'direct_to_owner' && c.deliveryAddress && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {t('merchant:consolidation.directToOwner')}: {c.deliveryAddress}
                    </span>
                  )}
                </div>
                {c.adminRemarks && (
                  <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    {t('merchant:consolidation.remarksLabel')}{c.adminRemarks}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    );
  };

  const renderEstates = () => {
    if (estatesLoading) return <Loading />;
    if (estatesError) return <ErrorBox message={estatesError} onRetry={fetchEstates} />;
    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('merchant:estates.intro')}</p>

        {!token && (
          <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800 dark:text-amber-200">
              <p className="font-bold">{t('merchant:estates.readonlyTitle')}</p>
              <p>{t('merchant:estates.readonlyBody')}</p>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex flex-col lg:flex-row lg:items-end gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 flex-1">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('merchant:estates.filterRegion')}</label>
                <select
                  value={estateRegionFilter}
                  onChange={(e) => {
                    setEstateRegionFilter(e.target.value as 'all' | 'HK' | 'KL' | 'NT');
                    setEstateDistrictFilter('all');
                    setEstateAreaFilter('all');
                  }}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-brand-500 outline-none text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="all">{t('merchant:estates.filterAll')}</option>
                  {REGIONS.map((r) => (
                    <option key={r.value} value={r.value}>{t(r.labelKey)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('merchant:estates.filterDistrict')}</label>
                <select
                  value={estateDistrictFilter}
                  onChange={(e) => {
                    setEstateDistrictFilter(e.target.value);
                    setEstateAreaFilter('all');
                  }}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-brand-500 outline-none text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  {districtOptions.map((d) => (
                    <option key={d} value={d === '全部' ? 'all' : d}>{d === '全部' ? t('merchant:estates.filterAll') : d}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('merchant:estates.filterArea')}</label>
                <select
                  value={estateAreaFilter}
                  onChange={(e) => setEstateAreaFilter(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-brand-500 outline-none text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  {areaOptions.map((a) => (
                    <option key={a} value={a === '全部' ? 'all' : a}>{a === '全部' ? t('merchant:estates.filterAll') : a}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('merchant:estates.filterType')}</label>
                <select
                  value={estateTypeFilter}
                  onChange={(e) => setEstateTypeFilter(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-brand-500 outline-none text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  {estateTypeOptions.map((tt) => (
                    <option key={tt} value={tt === '全部' ? 'all' : tt}>{tt === '全部' ? t('merchant:estates.filterAll') : tt}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('merchant:estates.searchLabel')}</label>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('merchant:estates.searchPlaceholder')}
                    value={estateSearch}
                    onChange={(e) => setEstateSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-brand-500 outline-none text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowCreateEstateModal(true)}
              disabled={!token}
              className="inline-flex items-center gap-1 bg-brand-600 hover:bg-brand-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2.5 rounded-lg text-sm font-bold transition"
            >
              <Plus className="w-4 h-4" /> {t('merchant:estates.addEstate')}
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">{t('merchant:estates.colName')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">{t('merchant:estates.colDistrict')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">{t('merchant:estates.colArea')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">{t('merchant:estates.colType')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">{t('merchant:estates.colActive')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-200">{t('merchant:estates.colAction')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {paginatedEstateSettings.map(row => {
                  const draft = estateDrafts[row.id] || { minOrderAmount: row.minOrderAmount || 0, isActive: row.isActive };
                  return (
                    <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3 text-gray-900 dark:text-white font-medium">{row.name}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{row.district || '—'}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{row.area || '—'}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{row.estateType || '—'}</td>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={draft.isActive}
                          onChange={(e) => updateEstateDraft(row.id, { isActive: e.target.checked })}
                          disabled={!token}
                          className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 disabled:opacity-60"
                        />
                      </td>
                      <td className="px-4 py-3">
                        {row.settingId ? (
                          <div className="inline-flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleSaveEstateSetting(row)}
                              disabled={!token || estateActionLoading}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-xs font-bold rounded-lg transition disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              <Save size={12} /> {t('merchant:estates.save')}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveEstateSetting(row)}
                              disabled={!token || estateActionLoading}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:text-gray-400 text-xs font-bold rounded-lg transition disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                              <Trash2 size={12} /> {t('merchant:estates.remove')}
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleJoinEstate(row)}
                            disabled={!token || estateActionLoading}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-xs font-bold rounded-lg transition disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            <Plus size={12} /> {t('merchant:estates.join')}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {paginatedEstateSettings.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                      {t('merchant:estates.noMatch')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {filteredEstateSettings.length > ESTATES_PER_PAGE && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-center items-center gap-3">
              <button
                disabled={estatePage <= 1}
                onClick={() => setEstatePage((p) => p - 1)}
                className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-sm hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                {t('merchant:estates.prevPage')}
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {t('merchant:estates.pageIndicator', { page: estatePage, totalPages: estateTotalPages })}
              </span>
              <button
                disabled={estatePage >= estateTotalPages}
                onClick={() => setEstatePage((p) => p + 1)}
                className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-sm hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                {t('merchant:estates.nextPage')}
              </button>
            </div>
          )}
        </div>

        {showCreateEstateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Plus className="w-5 h-5 text-brand-600" />
                  {t('merchant:estates.modalTitle')}
                </h2>
                <button
                  type="button"
                  onClick={() => setShowCreateEstateModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleCreateEstate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('merchant:estates.nameLabel')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={createEstateForm.name}
                    onChange={(e) => setCreateEstateForm({ ...createEstateForm, name: e.target.value })}
                    placeholder={t('merchant:estates.namePlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('merchant:estates.districtLabel')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={createEstateForm.district}
                    onChange={(e) => setCreateEstateForm({ ...createEstateForm, district: e.target.value, region: deriveRegionFromDistrict(e.target.value) })}
                    placeholder={t('merchant:estates.districtPlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('merchant:estates.areaLabel')}</label>
                  <input
                    type="text"
                    value={createEstateForm.area}
                    onChange={(e) => setCreateEstateForm({ ...createEstateForm, area: e.target.value })}
                    placeholder={t('merchant:estates.areaPlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('merchant:estates.typeLabel')}</label>
                  <input
                    type="text"
                    value={createEstateForm.estateType}
                    onChange={(e) => setCreateEstateForm({ ...createEstateForm, estateType: e.target.value })}
                    placeholder={t('merchant:estates.typePlaceholder')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 outline-none bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowCreateEstateModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    {t('merchant:estates.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={!token || createEstateLoading}
                    className="flex-1 px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:bg-gray-400 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-1"
                  >
                    {createEstateLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    {t('merchant:estates.addEstate')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderReviews = () => {
    if (ratingLoading) return <Loading />;
    if (ratingError) return <ErrorBox message={ratingError} onRetry={fetchRating} />;
    const average = Number(ratingData?.averageRating || 0);
    const count = Number(ratingData?.reviewCount || 0);
    const reviews = (ratingData?.latestReviews || []).slice(0, 5);
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('merchant:reviews.average')}</p>
            <div className="flex items-center gap-2">
              <span className="text-3xl font-bold text-gray-900 dark:text-white">{average > 0 ? average.toFixed(1) : '—'}</span>
              {average > 0 && <Star size={28} className="text-yellow-400 fill-yellow-400" />}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('merchant:reviews.count')}</p>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">{count}</p>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-base font-bold text-gray-900 dark:text-white">{t('merchant:reviews.latest')}</h2>
          </div>
          {reviews.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">{t('merchant:reviews.empty')}</div>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {reviews.map((r, idx) => (
                <li key={idx} className="px-5 py-4">
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        size={14}
                        className={i < r.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 dark:text-gray-600'}
                      />
                    ))}
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                      {r.createdAt ? formatDate(r.createdAt, dateLocale) : ''}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-line">{r.review || t('merchant:reviews.noComment')}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  };

  const renderReport = () => (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5">
        <label className="block text-sm font-bold text-gray-900 dark:text-white mb-2">{t('merchant:commission.chooseMonth')}</label>
        <div className="flex items-center gap-3">
          <input
            type="month"
            value={reportMonth}
            onChange={e => setReportMonth(e.target.value)}
            className="flex-1 min-w-0 px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-100 outline-none"
          />
          <button
            type="button"
            onClick={handleDownloadReport}
            disabled={reportLoading || !reportMonth}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-brand-600 hover:bg-brand-700 text-white text-sm font-bold rounded-lg transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {reportLoading ? <Loader2 size={16} className="animate-spin" /> : <FileSpreadsheet size={16} />}
            {t('merchant:commission.downloadExcel')}
          </button>
        </div>
        {reportError && <div className="mt-3"><ErrorBox message={reportError} /></div>}
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        {t('merchant:commission.reportHint')}
      </p>
    </div>
  );

  const renderSettings = () => {
    if (settingsLoading || !settings) return <Loading />;
    if (settingsError) return <ErrorBox message={settingsError} onRetry={fetchSettings} />;
    const dayLabels = [t('merchant:settings.day0'), t('merchant:settings.day1'), t('merchant:settings.day2'), t('merchant:settings.day3'), t('merchant:settings.day4'), t('merchant:settings.day5'), t('merchant:settings.day6')];
    return (
      <form onSubmit={handleSaveSettings} className="space-y-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Truck size={16} /> {t('merchant:settings.sectionDelivery')}
          </h3>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('merchant:settings.weeklyDays')}</label>
            <div className="flex flex-wrap gap-4">
              {dayLabels.map((label, idx) => (
                <label key={idx} className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={settings.deliveryDays.includes(idx)}
                    onChange={(e) => {
                      const current = new Set(settings.deliveryDays);
                      if (e.target.checked) current.add(idx); else current.delete(idx);
                      setSettings({ ...settings, deliveryDays: Array.from(current).sort((a, b) => a - b) });
                    }}
                    className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('merchant:settings.deliveryTime')}</label>
              <input
                type="text"
                value={settings.deliveryTime}
                onChange={(e) => setSettings({ ...settings, deliveryTime: e.target.value })}
                placeholder={t('merchant:settings.deliveryTimePlaceholder')}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('merchant:settings.cutoffTime')}</label>
              <input
                type="text"
                value={settings.deliveryCutoffTime}
                onChange={(e) => setSettings({ ...settings, deliveryCutoffTime: e.target.value })}
                placeholder={t('merchant:settings.cutoffPlaceholder')}
                pattern="^([0-1]?\d|2[0-3]):([0-5]\d)$"
                className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-brand-500"
              />
              {validateTime(settings.deliveryCutoffTime) && (
                <p className="text-xs text-red-600 mt-1">{t(validateTime(settings.deliveryCutoffTime)!)}</p>
              )}
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('merchant:settings.cutoffDays')}</label>
            <input
              type="number"
              min={0}
              max={7}
              value={settings.deliveryCutoffDaysBefore}
              onChange={(e) => setSettings({ ...settings, deliveryCutoffDaysBefore: Number(e.target.value) })}
              className="w-full sm:w-32 px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div className="mb-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('merchant:settings.minSpend')}</label>
            <input
              type="number"
              min={0}
              step={1}
              value={settings.minOrderAmount}
              onChange={(e) => setSettings({ ...settings, minOrderAmount: Number(e.target.value) })}
              className="w-full sm:w-48 px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-4">{t('merchant:settings.closedSection')}</h3>
          <div className="flex items-center gap-2 mb-4">
            <input
              id="merchant-closed"
              type="checkbox"
              checked={settings.isClosed}
              onChange={(e) => setSettings({ ...settings, isClosed: e.target.checked })}
              className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            <label htmlFor="merchant-closed" className="text-sm text-gray-700 dark:text-gray-300">{t('merchant:settings.closedToggle')}</label>
          </div>
          {settings.isClosed && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('merchant:settings.closedUntil')}</label>
                <input
                  type="date"
                  value={settings.closedUntil}
                  onChange={(e) => setSettings({ ...settings, closedUntil: e.target.value })}
                  className="w-full sm:w-auto px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('merchant:settings.closedMessage')}</label>
                <input
                  type="text"
                  value={settings.closedMessage}
                  onChange={(e) => setSettings({ ...settings, closedMessage: e.target.value })}
                  placeholder={t('merchant:settings.closedMessagePlaceholder')}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
            </div>
          )}
        </div>

        {settingsError && <ErrorBox message={settingsError} />}
        {settingsSuccess && (
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-green-700 dark:text-green-300 text-sm flex items-center gap-2">
            <CheckCircle size={18} />
            {settingsSuccess}
          </div>
        )}

        <button
          type="submit"
          disabled={settingsSaving}
          className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-brand-600 hover:bg-brand-700 text-white text-sm font-bold rounded-lg transition disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {settingsSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {t('merchant:settings.saveBtn')}
        </button>
      </form>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 bg-brand-600 rounded-full flex items-center justify-center text-white shadow-lg flex-shrink-0">
          <Store size={24} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('merchant:header.kicker')}</p>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{merchantName || t('merchant:header.fallbackTitle')}</h1>
          {merchantCode && <p className="text-sm text-gray-500 dark:text-gray-400">{t('merchant:header.gsCodeLabel')}{merchantCode}</p>}
        </div>
        {ratingData && Number(ratingData.averageRating || 0) > 0 && (
          <div className="flex items-center gap-1.5 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 px-3 py-1.5 rounded-xl flex-shrink-0">
            <Star size={16} className="text-yellow-500 fill-yellow-500" />
            <span className="text-sm font-bold text-yellow-700 dark:text-yellow-300">
              {Number(ratingData.averageRating || 0).toFixed(1)}
            </span>
            <span className="text-xs text-yellow-600 dark:text-yellow-400">
              ({Number(ratingData.reviewCount || 0)})
            </span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 pb-1 -mx-1 px-1">
        {TABS.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl text-xs sm:text-sm font-bold whitespace-nowrap transition ${
                active
                  ? 'bg-brand-600 text-white shadow'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Icon size={16} className="shrink-0" />
              <span className="hidden sm:inline">{t(tab.labelKey)}</span>
              <span className="sm:hidden">{t(tab.shortLabelKey)}</span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      {activeTab === 'dashboard' && renderDashboard()}
      {activeTab === 'orders' && renderOrders()}
      {activeTab === 'estates' && renderEstates()}
      {activeTab === 'reviews' && renderReviews()}
      {activeTab === 'report' && renderReport()}
      {activeTab === 'settings' && renderSettings()}

    </div>
  );
}
